import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";

/**
 * Enum pro převod barev na rgb
 * */
export enum Colors {
    blue,
    red,
    green,
    pink,
    purple,
    lime,
    brown,
    indigo,
    lightblue,
    cyan,
    teal,
    lightgreen,
    yellow,
    orange,
    deeppurple,
    amber,
    deeporange,
}

interface IColor {
    colorTheme: {
        dark: string;
        light: string;
    };
    name: string;
    className: string;
}

/**
 * Umožnuje pracovat s barevnou paletou MultiWebu, kterou získává z CSS stylů.
 * Použití pro speciální případy, kdy nelze předávat barvu elementu pomocí class atributu (např. grafy, které používají element canvas).
 */
export class MWColors {
    private static _instance: MWColors;

    /**
     * RegEx pro nalezení definice barev (využití např. při získávání barev z CSS property). Filtruje hex, jmenný, hsl(a), rgb(a) zápis barvy.
     *
     * Formát řetězce: tmavyOdstin svetlyOdstin jmenoBarvy selectorBarvy, tmavyOdstin svetlyOdstin jmenoBarvy selectorBarvy, ...
     * hsl(0, 65%, 48%) hsl(0, 78%, 62%) "Red" "red", rgba(340, 66, 47, 0.1) #684FAC "Pink" "pink", "purple" hsl(293, 61%, 63%) "Purple" "purple"
     *
     * Příklad výsledku pro jednu barvu:
     * hsl(0, 65%, 48%)
     * hsl(0, 78%, 62%)
     * "Red"
     * "red"
     */
    private readonly _findColorsRegEx = /("[a-z]+"|#[0-9a-f]{3,6}|(?:hsl[a]?|rgb[a]?)[(][^)]+[)]) ("[a-z]+"|#[0-9a-f]{3,6}|(?:hsl[a]?|rgb[a]?)[(][^)]+[)]) "([^"]+)" "([^"]+)"/gi;

    /** RegEx najde dvojité uvozovky v kombinaci s čárkou (označuje konec jedné barvy) v řetězci s barvami = počet barev. Používá se pro zpětné ověření, zda nechybí nějaká barva v rozparsované paletě MultiWeb barev */
    private readonly _findDoubleQuotationsAndCommas = /"\s*,/gi;

    /** RegEx najde všechny dvojité uvozovky */
    private readonly _findDoubleQuotations = /"/gi;

    /** RegEx najde výskyt formátu barvy zapsaný rgb nebo rgba */
    private readonly _findRgbRgbaColorFormat = new RegExp(/^rgb\((?:(?:\s*(?:0|[1-9]\d?|1\d\d?|2[0-4]\d|25[0-5])\s*),){2}(?:\s*(?:0|[1-9]\d?|1\d\d?|2[0-4]\d|25[0-5])\s*){1}\)$|^rgb\((?:(?:\s*(?:0|[1-9]\d?|100)%\s*),){2}(?:\s*(?:0|[1-9]\d?|100)%\s*){1}\)$|^rgba\((?:(?:\s*(?:0|[1-9]\d?|1\d\d?|2[0-4]\d|25[0-5])\s*),){3}(?:\s*(?:[01]|0\.\d\d*)\s*){1}\)$/gi);

    /** RegEx najde výskyt formátu barvy zapsaný hsl nebo hsla */
    private readonly _findHslHslaColorFormat = new RegExp(/^hsl\((?:\s*(?:0|[1-9]\d?|[1-2]\d\d?|3[0-5]\d?|360)\s*),(?:\s*(?:0|[1-9]\d?|100)%\s*),(?:\s*(?:0|[1-9]\d?|100)%\s*)\)$|^hsla\((?:\s*(?:0|[1-9]\d?|[1-2]\d\d?|3[0-5]\d?|360)\s*),(?:(?:\s*(?:0|[1-9]\d?|100)%\s*),){2}(?:\s*(?:[01]|0\.\d\d*)\s*){1}\)$/gi);

    /** RegEx najde výskyt formátu barvy zapsaný hexadecimálně nebo hexadecimálně se složkou transparentnosti */
    private readonly _findHexOrHexaColorFormat = new RegExp(/^#(?:[0-9a-fA-F]){3}\b$|^#(?:[0-9a-fA-F]){6}\b$|^#(?:[0-9a-fA-F]){8}\b$/gi);

    /** RegEx najde výskyt formátu barvy zapsaný jménem barvy (tzn. obsahuje pouze znaky a-z) */
    private readonly _findNameColorFormat = new RegExp(/^[a-zA-Z]+$/gi);

    /**
     * RegEx najde výskyt formátu barvy zapsaný rgb, rgba, hsl, hsla, hex, hexa nebo jménem barvy.
     * Příklad vyhovujícího formátu barvy:
     * rgb(255, 50, 0)
     * rgb(0%, 66%, 100%)
     * rgba(55, 255, 255, 0.55)
     * hsl(360, 60%, 0%)
     * hsla(0, 10%, 20%, 0.5)
     * #fff
     * #00FF00
     * #ff00ff80
     * Red
     * green
     */
    private readonly _findRgbHslHexNameColorFormat = new RegExp(this._findRgbRgbaColorFormat.source + "|" + this._findHslHslaColorFormat.source + "|" + this._findHexOrHexaColorFormat.source + "|" + this._findNameColorFormat.source);

    public readonly allColors: IColor[];
    public readonly primaryColors: IColor[];

    private constructor() {
        this.primaryColors = this._separateColors("--mw-colors-primary");
        const additionalColors = this._separateColors("--mw-colors-additional");
        this.allColors = [...this.primaryColors, ...additionalColors];
    }

    public static getInstance(): MWColors {
        if (!MWColors._instance) {
            this._instance = new MWColors();
        }
        return this._instance;
    }

    /**
     * Vrací barevnou paletu dle předané CSS proměnné, kterou hledá v selektoru ":root".
     * @param cssVariableName Název CSS proměnné.
     * @returns Pole objektů, kde každý objekt obsahuje: hodnotu barvy pro tmavý a světlý odstín, název barvy a název selektoru barvy.
     */
    private _separateColors(cssVariableName: string): IColor[] {
        const style = getComputedStyle(document.body);
        const cssColors: string = style.getPropertyValue(cssVariableName);
        const colors: IColor[] = [];

        if (!cssColors)
            throw new Error(`In CSS style is missing variables ${cssVariableName} that contains palette of MultiWeb colors.`);

        let matchColors: RegExpExecArray | null;
        while ((matchColors = this._findColorsRegEx.exec(cssColors)) !== null) {
            const [darkColor, lightColor, name, className] = matchColors.slice(1);

            if (!this._isCorrectColorFormat(darkColor) || !this._isCorrectColorFormat(lightColor) || StringUtils.isNullOrEmpty(name) || StringUtils.isNullOrEmpty(className)) {
                throw new Error(`Wrong definition of MultiWeb color, please check LESS sources for color: "${name}" (selector: "${className}" "${darkColor}" "${lightColor}").`);
            }

            colors.push({
                name: name,
                className: className,
                colorTheme: {
                    dark: darkColor.replace(this._findDoubleQuotations, ""),
                    light: lightColor.replace(this._findDoubleQuotations, ""),
                },
            });
        }

        const matchCommas = cssColors.match(this._findDoubleQuotationsAndCommas);

        if ((matchCommas !== null && (matchCommas.length + 1) !== colors.length) || (matchCommas === null && colors.length < 1)) // jedna čárka se přidává, protože za poslední barvou v definici nebývá čárka
            throw new Error("Different number of separated MultiWeb colors and colors defined in CSS file. Please check correct color definition (especially individual division by commas).");

        return colors;
    }

    /**
     * Vrací odstín zadané barvy dle aktuálního tématu stránky.
     * @param colorClassName Název selektoru barvy, pro kterou chceme získat odstín dle aktuálního tématu stránky (např. "red", "color-primary", "bw"... definované v CSS).
     * @returns Vrací hodnotu barvy nebo undefined, pokud se selektor barvy v paletě nevyskytuje. Barva je vyjádřena v hsl, rgb, # nebo jméno barvy (záleží, jakou formou byla barva definována v CSS stylech).
     */
    public getThemeColor(colorClassName: string): string | undefined {
        const index = this.allColors?.findIndex(color => StringUtils.compareCaseInsensitive(color.className, colorClassName));

        if (!index && index === -1)
            return undefined;

        const isThemeDark = document.querySelector("html")?.classList?.contains("theme-dark") || false;

        return isThemeDark ? this.allColors[index].colorTheme.light : this.allColors[index].colorTheme.dark;
    }

    /**
     * Ověří, zda barva existuje v paletě MultiWebu.
     * Porovnává se pouze selektor barvy tzn. "red", "color-primary", "bw", ...).
     * Pozn. selektory barev palety MultiWebu můžeme dohledat v CSS stylech pod proměnnou --mw-colors-primary nebo --mw-colors-additional.
     * @param colorClassName Název selektoru barvy, kterou chceme ověřit.
     * @returns Vrací true, pokud se barva v paletě MultiWebu vyskytuje, false pokud nevyskytuje.
     */
    public includesColor(colorClassName: string): boolean {
        return this.allColors && this.allColors.some(color => StringUtils.compareCaseInsensitive(color.className, colorClassName));
    }

    /**
     * Ověří, zda formát barvy vyhovuje CSS specifikaci (kontroluje rgb, rgba, hsl, hsla, hex, hexa, name)
     * @param colorValue Hodnota barvy, pro kterou chceme ověřit formát.
     * @returns Vrací true, když vyhovuje CSS specifikaci barev, false pokud nevyhovuje.
     */
    private _isCorrectColorFormat(colorValue: string): boolean {
        if (!colorValue) {
            return false;
        }

        const match = colorValue.match(this._findRgbHslHexNameColorFormat);

        return !(match === null);
    }
}
